import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { useDispatch } from 'react-redux';
import theme, { BACKDROP_FILTERS } from 'src/styles/theme';
import {
  AuthenticationFlow,
  AuthenticationFlowPageId,
} from './AuthenticationFlow';
import { keyframes } from '@emotion/react';
import { useState } from 'react';
import { reset as resetAuthState } from './store/authSlice';
import { useMobile } from 'src/hooks/useMobile';

type AuthenticationModalProps = {
  open: boolean;
  onClose: () => void;
  body: JSX.Element;
  startPage?: AuthenticationFlowPageId;
  startPageProps?: Record<string, unknown>;
};

const modalBounce = keyframes`
from, to { transform: translate(-50%, -50%) scale(1) }
50% { transform: translate(-50%, -50%) scale(1.08) }
`;

export function AuthenticationModal({
  open,
  onClose,
  body,
  startPage,
  startPageProps,
}: AuthenticationModalProps) {
  const dispatch = useDispatch();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const isMobile = useMobile();

  if (!open || !onClose || !body) return null;
  const handleClose = () => {
    if (!startPageProps?.fromGetUpdates) {
      dispatch(resetAuthState());
    }
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        } else {
          // Bounce animation
          setShouldAnimate(true);
        }
      }}
      disableEscapeKeyDown={true}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: isMobile ? '536px' : '836px',
          // We want the inner div with the border styling to take up the full width and height
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          padding: '2px', // Should match the padding of '&:before'
          animation: shouldAnimate ? `${modalBounce} .4s` : 'none',
        }}
        onAnimationEnd={() => setShouldAnimate(false)}
      >
        <Box
          sx={{
            background: theme.palette.background.elevation[10],
            backdropFilter: BACKDROP_FILTERS[13],
            borderRadius: '10px',
            zIndex: 0,
            position: 'relative',
            width: '100%',
            '&:before': {
              padding: '2px',
              borderRadius: '10px',
              zIndex: -1,
              content: '""',
              position: 'absolute',
              inset: '0',
              background: theme.palette.primary.vertical,
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'exclude',
              WebkitMaskComposite: 'xor',
            },
          }}
        >
          <AuthenticationFlow
            closeModal={handleClose}
            startPage={startPage}
            startPageProps={startPageProps}
          />
        </Box>
      </Box>
    </Modal>
  );
}
